import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Camera, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { getPosition } from "@/api";
import { useAuth } from "@/AuthContext";

export default function CheckIn() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [photoData, setPhotoData] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/attendance/status").then((response) => setStatus(response.data))
      .catch(() => toast.error("Unable to load attendance status"));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      let position = null;
      if (status.config.gps_enabled) position = await getPosition();
      if (status.config.gps_enabled && !position) throw new Error("Enable location access to check in");
      if (status.config.selfie_enabled && !photoData) throw new Error("Take a selfie before checking in");
      await api.post("/attendance/checkin", {
        lat: position?.lat ?? null,
        lng: position?.lng ?? null,
        photo_data: photoData || null,
      });
      window.dispatchEvent(new Event("attendance-updated"));
      toast.success("Checked in");
      navigate(location.state?.from?.pathname || (user.role === "team_leader" ? "/app" : "/admin"), { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Check-in failed");
    } finally {
      setBusy(false);
    }
  };

  const selectSelfie = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoData(String(reader.result));
    reader.readAsDataURL(file);
  };

  if (!status) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading attendance…</div>;
  return (
    <main className="min-h-screen bg-background grid place-items-center px-5 py-10" data-testid="attendance-checkin-screen">
      <section className="w-full max-w-lg rounded-[28px] border border-border/80 bg-card/80 p-6 shadow-layered backdrop-blur-xl sm:p-8">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-tint text-primary shadow-soft">
          <ShieldCheck size={26} />
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{status.date}</p>
        <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight">Check In</h1>
        <p className="mt-2 text-sm text-muted-foreground">Good day, {user.name?.split(" ")[0]}. Confirm your start time to continue.</p>
        <div className="mt-6 space-y-4 rounded-2xl border border-border/70 bg-muted/30 p-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><MapPin size={17} /></span>
            <span>{status.config.gps_enabled ? `Location required${status.config.geofence_radius_m ? ` · ${status.config.geofence_radius_m}m geofence` : ""}` : "Location capture is off"}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-info-tint text-info"><Camera size={17} /></span>
            <span>{status.config.selfie_enabled ? "Selfie required" : "Selfie is optional"}</span>
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground" htmlFor="attendance-selfie">Selfie {status.config.selfie_enabled ? "(required)" : "(optional)"}</label>
            <input id="attendance-selfie" data-testid="attendance-selfie" type="file" accept="image/*" capture="user"
              onChange={selectSelfie} className="mt-1 block w-full rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm" />
            {photoData && <img src={photoData} alt="Check-in selfie preview" className="mt-3 h-36 w-36 rounded-2xl object-cover shadow-soft ring-4 ring-primary/10" />}
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between gap-4 text-xs text-muted-foreground">
          <span>Shift starts {status.config.shift_start}; grace {status.config.grace_minutes} min</span>
          <span className="rounded-full border border-border bg-card/80 px-2.5 py-1 font-medium text-foreground">{new Date(status.server_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <button type="button" onClick={submit} disabled={busy} data-testid="attendance-checkin-confirm"
          className="relative mt-6 flex h-16 w-full items-center justify-center overflow-hidden rounded-[22px] bg-primary font-semibold text-primary-foreground shadow-soft transition-colors hover:bg-primary/90 disabled:opacity-60">
          <span className="relative z-10">{busy ? "Checking in…" : "Check In and Continue"}</span>
        </button>
      </section>
    </main>
  );
}
